import { yupResolver } from "@hookform/resolvers/yup";
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    TextField,
} from "@mui/material";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import {
    formatCurrencyToShow,
    parseCurrencyToNumber,
} from "../../helpers/currency";
import { Withdrawal } from "../../interfaces/withdrawal";
import { changeAmountSchema } from "../../validators/schemas/changeAmount";

interface WithdrawalApproveConfirmModalProps {
    onConfim?: (data: Withdrawal) => void;
    onCancel?: () => void;
    open: boolean;
    data?: Withdrawal | null;
}

type FormData = {
    amount: string;
    admin_note: string;
};

export function WithdrawalApproveConfirmModal({
    open,
    onCancel,
    onConfim,
    data,
}: WithdrawalApproveConfirmModalProps) {
    const {
        handleSubmit,
        control,
        setValue,
        reset,
        formState: { errors, isValid },
    } = useForm<FormData>({
        mode: "onChange",
        resolver: yupResolver(changeAmountSchema),
    });

    const handleClose = () => {
        onCancel?.();
        reset();
    };

    const onSubmit = (values: FormData) => {
        if (data) {
            const amount = parseCurrencyToNumber(values.amount).toString();
            onConfim?.({ ...data, amount, admin_note: values.admin_note });
        }

        handleClose();
    };

    useEffect(() => {
        if (data) {
            setValue("amount", formatCurrencyToShow(data?.amount) || "");
            setValue("admin_note", data?.admin_note || "");
        }

        return () => reset();
    }, [data]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            disableRestoreFocus
        >
            <DialogTitle variant="h6" component="h2">
                Aprovar Saque
            </DialogTitle>

            <DialogContent>
                <Box gap={2} display="flex" flexDirection="column" pt={2}>
                    <Controller
                        name="amount"
                        control={control}
                        render={({ field: { onChange, ...field } }) => (
                            <TextField
                                label="Valor"
                                size="small"
                                fullWidth
                                placeholder="R$ 5.30"
                                error={!!errors.amount}
                                helperText={errors.amount?.message}
                                InputProps={{ endAdornment: "R$" }}
                                onChange={({ target: { value } }) => {
                                    const mv = formatCurrencyToShow(
                                        value,
                                        true
                                    );
                                    onChange(mv);
                                }}
                                {...field}
                            />
                        )}
                    />

                    <Controller
                        name="admin_note"
                        control={control}
                        render={({ field: { onChange, ...field } }) => (
                            <TextField
                                label="Observação"
                                size="small"
                                fullWidth
                                multiline
                                rows={3}
                                placeholder="Adicione uma observação (Opcional)"
                                error={!!errors.admin_note}
                                helperText={errors.admin_note?.message}
                                inputProps={{ maxLength: 100 }}
                                onChange={onChange}
                                {...field}
                            />
                        )}
                    />
                </Box>

                <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
                    <Button onClick={handleClose} color="inherit">
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!isValid}
                        onClick={handleSubmit(onSubmit)}
                    >
                        Confirmar
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
